import { indorsoftRuAPI } from "../indorsoftRuApi"

const PRODUCT_NAME = "IndorGeo";
const INDOR_GEO_CATEGORY_ID = "125";
export const CRITICALITY_IDS = {
    low: 101,
    medium: 102,
    high: 103
}

/**
 * Определяет SITE_ID на основе домена текущей страницы
 * Используется для изоляции обращений по сайтам
 * Возвращает hostname текущей страницы
 */
function getSiteId(): string {
    if (typeof window === 'undefined') {
        return ''; // Значение по умолчанию для SSR
    }
    
    return window.location.hostname;
}

export interface SupportTicket {
    ID: string;
    DATE_CLOSE: string | null;
    DATE_CREATE: string;
    LAST_MESSAGE_DATE: string;
    TITLE: string;
    CRITICALITY_NAME: string;
    CRITICALITY_ID: string;
    /**
     * "Y" — последнее сообщение от поддержки, "N" — от пользователя
     */
    LAST_MESSAGE_BY_SUPPORT_TEAM?: "Y" | "N";

    /**
     * Идентификатор последнего сообщения поддержки, прочитанного пользователем
     */
    LAST_READ_SUPPORT_MESSAGE_ID?: number;

    /**
     * Идентификатор последнего сообщения от поддержки в тикете
     */
    LAST_SUPPORT_MESSAGE_ID?: number;

    /**
     * Есть ли непрочитанные сообщения поддержки
     * "Y" — есть непрочитанные, "N" — все прочитаны
     */
    HAS_UNREAD_SUPPORT_MESSAGES?: "Y" | "N";
}

export interface SupportTicketsResponse {
    tickets: SupportTicket[];
    rowsCount: number;
    ticketsCount: number;
    currentPage: string;
}

export interface SupportTicketDetail {
    ID: string;
    TITLE: string;
    DATE_CREATE: string;
    TIMESTAMP_X: string;
    STATUS_NAME: string;
    CRITICALITY_NAME: string;
    CRITICALITY_ID: string;
    CATEGORY_NAME: string;

    /**
     * Идентификатор последнего сообщения поддержки, прочитанного пользователем
     */
    LAST_READ_SUPPORT_MESSAGE_ID?: number;

    /**
     * Идентификатор последнего сообщения от поддержки
     */
    LAST_SUPPORT_MESSAGE_ID?: number;

    /**
     * Идентификатор первого непрочитанного сообщения
     */
    FIRST_UNREAD_SUPPORT_MESSAGE_ID?: number;

    /**
     * Дата закрытия тикета (если не null — обращение закрыто)
     */
    DATE_CLOSE?: string | null;

    /**
     * Есть ли непрочитанные сообщения поддержки
     */
    HAS_UNREAD_SUPPORT_MESSAGES?: "Y" | "N";
}

export interface SupportTicketFile {
    ID: string;
    HASH: string;
    NAME: string;
    FILE_SIZE: string;
}

export interface SupportTicketMessage {
    ID: string;
    DATE_CREATE: string;
    IS_HIDDEN: "Y" | "N";
    MESSAGE: string;
    FILES: SupportTicketFile[];
    /**
     * "Y" — сообщение от поддержки, "N" — от пользователя
     */
    MESSAGE_BY_SUPPORT_TEAM: "Y" | "N";
}

export interface UnreadTicketStatsResponse {
    hasUnread: boolean;
    unreadTicketsCount: number
}

export interface SupportTicketByIdResponse {
    ticket: SupportTicketDetail;
    messages: SupportTicketMessage[];
}

export interface CreateTicketResponse {
    success: boolean;
    ticketId: number;
}

export interface AddMessageResponse {
    success: boolean;
    ticketId: number;
}

export interface CheckModuleResponse {
    module?: {
        id: string;
        installed: boolean;
        version: string | null;
    };
    error?: string;
}

export interface SupportModuleStatusResponse {
    isActive: boolean;
}

export const supportApiSlice = indorsoftRuAPI.injectEndpoints({
    endpoints: builder => ({
        checkSupportModuleStatus: builder.query<SupportModuleStatusResponse, void>({
            query: () => ({
                url: 'api/support/check_module.php',
                method: 'GET',
                params: { SITE_ID: getSiteId() },
                credentials: 'include',
            }),
            transformResponse: (response: CheckModuleResponse): SupportModuleStatusResponse => {
                // Если есть ошибка в ответе, модуль неактивен
                if (response.error) {
                    return { isActive: false };
                }
                
                // Проверяем установленность модуля
                if (response.module) {
                    return { isActive: response.module.installed };
                }
                
                // Если структура ответа неожиданная, считаем модуль неактивным
                return { isActive: false };
            },
            keepUnusedDataFor: 60, // Кэшируем на 60 секунд
        }),
        getUnreadTicketStats: builder.query<UnreadTicketStatsResponse, void>({
            query: (args) => ({
                url: 'api/support/unread_stats.php',
                params: { 
                    CATEGORY_ID: INDOR_GEO_CATEGORY_ID,
                    SITE_ID: getSiteId(),
                },
                method: 'GET',
                credentials: 'include',
            }),
            keepUnusedDataFor: 0,
        }),
        createSupportTicket: builder.mutation<CreateTicketResponse, CreateTicketArgs>({
            query: (args) => ({
                url: 'api/support/create_ticket.php',
                method: 'POST',
                body: buildTicketFormData(args),
                credentials: 'include',
            }),
        }),
        getSupportTickets: builder.query<SupportTicketsResponse, void>({
            query: () => ({
                url: 'api/support/ticket_list.php',
                params: { 
                    CATEGORY_ID: INDOR_GEO_CATEGORY_ID,
                    SITE_ID: getSiteId(),
                },
                credentials: 'include',
            }),
            keepUnusedDataFor: 0,
        }),
        getSupportTicketById: builder.query<SupportTicketByIdResponse, string | number>({
            query: (id) => ({
                url: 'api/support/ticket_detail.php',
                params: { 
                    ID: id,
                    SITE_ID: getSiteId(),
                },
                credentials: 'include',
            }),
            keepUnusedDataFor: 0,
        }),
        addSupportTicketMessage: builder.mutation<AddMessageResponse, AddTicketMessageArgs>({
            query: (args) => ({
                url: 'api/support/add_message.php',
                method: 'POST',
                body: buildTicketMessageFormData(args),
                credentials: 'include',
            }),
        }),
        markSupportTicketMessagesRead: builder.mutation<{ success: boolean }, { id: string | number; lastSeenMessageId: number; sessid: string }>({
            query: ({ id, lastSeenMessageId, sessid }) => {
                const formData = new FormData();
                formData.append('ID', String(id));
                formData.append('lastSeenMessageId', String(lastSeenMessageId));
                formData.append('sessid', sessid);
                formData.append('SITE_ID', getSiteId());

                return {
                    url: 'api/support/mark_read.php',
                    method: 'POST',
                    body: formData,
                    credentials: 'include',
                };
            },
        }),
    })
})

export const {
    useCheckSupportModuleStatusQuery,
    useGetUnreadTicketStatsQuery,
    useCreateSupportTicketMutation,
    useGetSupportTicketsQuery,
    useGetSupportTicketByIdQuery,
    useAddSupportTicketMessageMutation,
    useMarkSupportTicketMessagesReadMutation,
} = supportApiSlice

export interface TicketFormValues {
    title: string;
    message: string;
    criticality: 'low' | 'medium' | 'high';
    category?: string;
}

export type CreateTicketArgs = {
    values: TicketFormValues;
    files?: File[];
    sessid: string;
};

export interface AddTicketMessageArgs {
    id: string | number;
    message: string;
    files?: File[];
    sessid: string;
}

/**
 * Формирует FormData для создания тикета.
 * Новый API принимает только необходимые поля.
 */
export const buildTicketFormData = ({
    values,
    files,
    sessid,
}: CreateTicketArgs): FormData => {
    const { title, message, criticality } = values ?? {}
    const titleWithProductName = `${PRODUCT_NAME} - ${title}`

    const formData = new FormData();

    // Обязательные поля для нового API
    formData.append('sessid', sessid);
    formData.append('TITLE', titleWithProductName);
    formData.append('MESSAGE', message);
    formData.append('CATEGORY_ID', INDOR_GEO_CATEGORY_ID);
    formData.append('SITE_ID', getSiteId());

    // Критичность (опционально)
    if (criticality) {
        const criticalityId = CRITICALITY_IDS[criticality];
        formData.append('CRITICALITY_ID', `${criticalityId}`);
    }

    // Файлы
    if (files?.length) {
        files.forEach((file, index) => {
            formData.append(`FILE_${index}`, file);
        });
        // files_counter должен быть на 1 меньше количества файлов (индексация с 0)
        formData.append('files_counter', String(files.length - 1));
    }

    return formData;
};

/**
 * Формирует FormData для добавления сообщения в тикет.
 * Новый API принимает только необходимые поля.
 */
const buildTicketMessageFormData = ({
    id,
    message,
    sessid,
    files,
}: AddTicketMessageArgs): FormData => {
    const formData = new FormData();

    // Обязательные поля для нового API
    formData.append('sessid', sessid);
    formData.append('ID', String(id));
    formData.append('MESSAGE', message);
    formData.append('SITE_ID', getSiteId());

    // Файлы
    if (files?.length) {
        files.forEach((file, index) => {
            formData.append(`FILE_${index}`, file);
        });
        // files_counter должен быть на 1 меньше количества файлов (индексация с 0)
        formData.append('files_counter', String(files.length - 1));
    }

    return formData;
};